/**
 * Conservazione della formattazione dei template email.
 *
 * L'editor tiene solo ciò che il suo schema conosce: senza queste estensioni, aprire un
 * template impaginato lo ridurrebbe a testo nudo, perché gli stili inline dei paragrafi
 * verrebbero scartati e i contenitori con essi.
 *
 * Nelle email la formattazione DEVE essere inline (i client non applicano CSS esterno),
 * quindi preservare l'attributo `style` non è una comodità: è la condizione perché un
 * template resti tale dopo la prima modifica.
 */
import { Extension, Node, mergeAttributes } from '@tiptap/react';

/** Nodi del documento che possono portare uno stile inline proprio. */
const STYLED_TYPES = ['paragraph', 'heading', 'listItem', 'bulletList', 'orderedList'];

/**
 * Aggiunge l'attributo `style` ai nodi di testo, leggendolo e riscrivendolo invariato.
 * Serve a non perdere colori, dimensioni e spaziature impostati nel template.
 */
export const InlineStyle = Extension.create({
  name: 'inlineStyle',

  addGlobalAttributes() {
    return [
      {
        types: STYLED_TYPES,
        attributes: {
          style: {
            default: null,
            parseHTML: (element) => element.getAttribute('style'),
            renderHTML: (attributes) => (attributes.style ? { style: attributes.style } : {}),
          },
        },
      },
    ];
  },
});

/**
 * Contenitore generico (`<div>`): è l'ossatura dei template email, che impaginano con
 * riquadri annidati. Senza questo nodo i contenitori sparirebbero e con loro sfondi,
 * bordi e spaziature.
 *
 * `content: 'block+'` lo rende annidabile: un riquadro può contenerne altri.
 */
export const EmailContainer = Node.create({
  name: 'emailContainer',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
        renderHTML: (attributes) => (attributes.style ? { style: attributes.style } : {}),
      },
      align: {
        default: null,
        parseHTML: (element) => element.getAttribute('align'),
        renderHTML: (attributes) => (attributes.align ? { align: attributes.align } : {}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div',
        // Il bottone CTA è anch'esso un <div>, ma ha un nodo suo: va lasciato a quello,
        // altrimenti perderebbe testo, colore e collegamento.
        getAttrs: (element) => (element.hasAttribute('data-email-button') ? false : null),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes), 0];
  },
});

export default { InlineStyle, EmailContainer };
